import Avatar from 'components/base/Avatar';
import { useState, useEffect } from 'react';
import { Card, Dropdown, Nav, Form } from 'react-bootstrap';
import avatar from 'assets/img/team/72x72/57.webp';
import FeatherIcon from 'feather-icons-react';
import { Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { UilBuilding } from '@iconscout/react-unicons';

// ---------------- Types ----------------
type UserRole = 'super_user' | 'company_admin' | 'user';

interface Company {
  id: string;
  name: string;
}

interface User {
  name: string;
  email: string;
  role: UserRole;
  companyId?: string | null;
  companies?: Company[];
  isLoggedIn: boolean;
}

// ---------------- Utility ----------------
const getReadableRole = (role?: UserRole | null) => {
  switch (role) {
    case 'super_user':
      return 'Super Admin';
    case 'company_admin':
      return 'Company Admin';
    case 'user':
      return 'User';
    default:
      return 'Unknown Role';
  }
};

// ---------------- Component ----------------
interface ProfileDropdownMenuProps {
  className?: string;
}

const ProfileDropdownMenu = ({ className }: ProfileDropdownMenuProps) => {
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [companies, setCompanies] = useState<Company[]>([]);

  // Load user from sessionStorage
  useEffect(() => {
    const userStr = sessionStorage.getItem('user');
    if (userStr) {
      try {
        const parsedUser: User = JSON.parse(userStr);
        setUser(parsedUser);

        const companyList = Array.isArray(parsedUser.companies)
          ? parsedUser.companies
          : [];
        setCompanies(companyList);

        // Determine selected company
        if (parsedUser.role === 'super_user') {
          setSelectedCompanyId(
            parsedUser.companyId &&
              companyList.some(c => c.id === parsedUser.companyId)
              ? parsedUser.companyId
              : companyList[0]?.id || ''
          );
        } else {
          setSelectedCompanyId(parsedUser.companyId || '');
        }
      } catch (err) {
        console.error('Failed to parse user:', err);
      }
    }
  }, []);

  const handleCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCompanyId = e.target.value;
    setSelectedCompanyId(newCompanyId);
    navigate(`/company/${encodeURIComponent(newCompanyId)}/dashboard`);
  };

  if (!user) return null; // or show a spinner/loading

  const displayName = user.name || 'User';
  const displayEmail = user.email || 'user@example.com';
  const displayRole = getReadableRole(user.role);

  const selectedCompanyName =
    companies.find(c => c.id === selectedCompanyId)?.name || 'Unknown Company';

  const navItems = [
    {
      label: 'Profile',
      icon: 'user',
      to: '/profile',
      roles: ['super_user', 'company_admin', 'user'] as UserRole[]
    },
    {
      label: 'Dashboard',
      icon: 'pie-chart',
      to: '/',
      roles: ['super_user', 'company_admin', 'user'] as UserRole[]
    }
  ].filter(item => item.roles.includes(user.role));

  return (
    <Dropdown.Menu
      align="end"
      className={classNames(
        className,
        'navbar-top-dropdown-menu navbar-dropdown-caret py-0 dropdown-profile shadow border'
      )}
    >
      <Card className="position-relative border-0">
        <Card.Body className="p-0">
          <div className="d-flex flex-column align-items-center justify-content-center gap-2 pt-4 pb-3">
            <Avatar src={avatar} size="xl" />
            <h5 className="text-body-emphasis mb-0">{displayName}</h5>
            <small className="text-body-secondary fw-semibold text-uppercase">
              {displayRole}
            </small>
            <small className="text-body-secondary">{displayEmail}</small>
          </div>

          <Nav className="nav flex-column mb-2 pb-1 px-3">
            {navItems.map(item => (
              <Nav.Item key={item.label}>
                <Nav.Link
                  as={Link}
                  to={item.to}
                  className="d-flex align-items-center py-2 px-0"
                  onClick={e => {
                    if (!user.isLoggedIn) {
                      e.preventDefault();
                      navigate('/login');
                    }
                    document.body.click(); // Close dropdown
                  }}
                >
                  <FeatherIcon
                    icon={item.icon}
                    size={16}
                    className="me-2 text-body"
                  />
                  <span className="text-body-highlight">{item.label}</span>
                </Nav.Link>
              </Nav.Item>
            ))}

            {user.role === 'super_user' && companies.length > 0 ? (
              <div key="company" className="mt-3 px-0">
                <div className="d-flex align-items-center mb-1 px-3">
                  <UilBuilding size={16} className="me-2 text-primary" />
                  <strong className="text-muted small">Company</strong>
                </div>
                <Form.Select
                  value={selectedCompanyId}
                  onChange={handleCompanyChange}
                  size="sm"
                  className="rounded shadow-sm border-1 px-3"
                  style={{
                    backgroundColor: '#f8f9fa',
                    fontWeight: 500,
                    cursor: 'pointer',
                    maxWidth: '100%'
                  }}
                >
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
            ) : companies.length > 0 ? (
              <Nav.Item key="company">
                <div
                  className="d-flex align-items-center py-2 px-0 nav-link"
                  style={{ gap: '6px' }}
                >
                  <UilBuilding size={16} className="text-dark" />
                  <span>{selectedCompanyName}</span>
                </div>
              </Nav.Item>
            ) : null}
          </Nav>
        </Card.Body>

        <Card.Footer className="p-3 border-top border-translucent">
          <Link
            to="/sign-out"
            className="btn btn-outline-secondary d-flex justify-content-center w-100"
            onClick={() => sessionStorage.removeItem('user')}
          >
            <FeatherIcon icon="log-out" className="me-2" size={16} />
            Sign Out
          </Link>
        </Card.Footer>
      </Card>
    </Dropdown.Menu>
  );
};

export default ProfileDropdownMenu;
